import {Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {PasskeyService} from '../../_services';
import {completePostLogin} from '../models/post-login';
import {environment} from '../../../environments/environment';

@Component({
  moduleId: module.id,
  selector: 'app-passkey-recovery',
  templateUrl: `../views/passkey-recovery.component.html`,
  styleUrls: ['../styles/auth-style.scss'],
  providers: [MessageService]
})
export class PasskeyRecoveryComponent implements OnInit {
  public logoUrl = environment.logoUrl;
  public loginToken: string = null;
  public code = '';
  public verifying = false;

  constructor(
    private router: Router,
    private passkeyService: PasskeyService,
    private messageService: MessageService,
    private translate: TranslateService
  ) {
    const navigation = this.router.getCurrentNavigation();
    const state = navigation && navigation.extras && navigation.extras.state;
    if (state) {
      this.loginToken = (state as any).loginToken;
    }
  }

  ngOnInit() {
    // Reached directly, by refresh or a pasted URL, there is no token and no
    // way to get one here. Send them back rather than showing a dead screen.
    if (!this.loginToken) {
      this.router.navigate(['/login']);
    }
  }

  verify() {
    // `verifying` gates the button via [disabled], but (keyup.enter) on the
    // input isn't disabled - without this guard, two quick Enter presses
    // fire two requests and burn two of the five budgeted attempts on one
    // bounce.
    if (this.verifying || !this.code) {
      return;
    }
    this.verifying = true;
    this.passkeyService.loginRecovery(this.loginToken, this.code).subscribe(
      response => {
        this.verifying = false;
        this.finishLogin(response);
      },
      error => {
        this.verifying = false;
        // error is a genuine HttpErrorResponse here (see PasskeyService.
        // loginRecovery()'s constructor comment for why that isn't the
        // default for calls made through the normal, interceptor-wrapped
        // HttpClient in this app).
        if (error.status === 429) {
          // Two different things answer 429 on this call and they mean
          // opposite things, so the status alone is not enough to branch on:
          //
          //  - PasskeyController::loginRecovery()'s own exhausted-budget
          //    reply, which carries error.code =
          //    'recovery_attempts_exhausted'. The attempt budget on this
          //    login_token is gone and the token is burned - even a correct
          //    code would now be refused, so there is nothing to retry on
          //    this screen and they need a fresh token.
          //  - Laravel's route throttle (throttle:30,1), which carries no
          //    code. It burned nothing: the codes are fine, the token is
          //    fine, and the only thing wrong is the request rate. It fires
          //    on a shared office IP, because Laravel keys the limiter on
          //    sha1(domain|ip) with no route in the signature, so every 2FA
          //    user behind one NAT shares the bucket. Telling those users
          //    their recovery codes are spent and bouncing them - which is
          //    what this screen used to do - is both wrong and alarming.
          if (this.isAttemptBudgetExhausted(error)) {
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('Error!'),
              detail: this.translate.instant('too many attempts please log in again')
            });
            setTimeout(() => this.router.navigate(['/login']), 2500);
            return;
          }

          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('Error!'),
            detail: this.translate.instant('too many requests please wait a moment')
          });
          return;
        }
        // Unknown, wrong, consumed and expired codes/tokens all return 403
        // with an identical message by design, so this covers all of them.
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('Error!'),
          detail: this.translate.instant('that recovery code is not valid')
        });
      }
    );
  }

  tryPasskeyAgain() {
    this.router.navigate(['/passkey-verify'], {state: {loginToken: this.loginToken, hasRecoveryCodes: true}});
  }

  backToLogin() {
    this.router.navigate(['/login']);
  }

  /**
   * True only for the api's own "this login_token's recovery-code budget is
   * spent" 429, never for a framework rate-limit 429. loginRecovery() goes
   * through PasskeyService's rawHttp, so `error` really is an
   * HttpErrorResponse and `error.error` really is the parsed JSON body.
   */
  private isAttemptBudgetExhausted(error: any): boolean {
    const body = error && error.error;
    const code = body && body.error && body.error.code;
    return code === 'recovery_attempts_exhausted';
  }

  // The whole post-login sequence (temp-password redirect, currentUser write,
  // language, permission ladder) is shared with login.component.ts and
  // passkey-verify.component.ts - see post-login.ts. login-recovery returns
  // exactly what /login returns on success, minus the `token` on a
  // temp-password response, which is why this path routes to /login rather
  // than /password-reset/:token.
  //
  // JS-1396 follow-up: `recovery_code_used` is the precise signal that this
  // device has no usable passkey (see PasskeyController::loginRecovery()).
  // overrideDestination detours the final navigation through the
  // add-a-passkey prompt instead of straight to the resolved destination -
  // the temp-password bail-out, the session write and the language switch
  // all still run exactly as they do on every other post-login path.
  private finishLogin(response: any) {
    completePostLogin(response, {
      router: this.router,
      translate: this.translate,
      tempPasswordDestination: 'login',
      overrideDestination: response.recovery_code_used ? '/passkey-add-device' : undefined
    });
  }
}
